import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { apiUrl } from '../utils/apiConfig';
import { PREFETCH_KEYS, readPrefetch } from '../utils/preloadApp';

const ThemeContext = createContext({
  theme: 'default',
  activatedAt: '',
  label: '',
  ready: false,
  refreshTheme: async () => {},
});

const SEEN_KEY = 'kkt_theme_intro_seen';

export function readIntroSeenMap() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function markIntroSeen(theme, activatedAt) {
  if (!theme || theme === 'default' || !activatedAt) return;
  try {
    const map = readIntroSeenMap();
    map[theme] = activatedAt;
    localStorage.setItem(SEEN_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function shouldShowIntro(theme, activatedAt) {
  if (!theme || theme === 'default' || !activatedAt) return false;
  const map = readIntroSeenMap();
  return map[theme] !== activatedAt;
}

function applyDomTheme(theme) {
  const root = document.documentElement;
  if (!theme || theme === 'default') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
}

function normalizeTheme(data) {
  const theme = String(data?.theme || 'default').toLowerCase();
  return {
    theme: ['default', 'school_year', 'snow'].includes(theme) ? theme : 'default',
    activatedAt: String(data?.activated_at || ''),
    label: String(data?.label || ''),
  };
}

export function ThemeProvider({ children }) {
  const [state, setState] = useState(() => {
    const cached = readPrefetch(PREFETCH_KEYS.theme, 30 * 60 * 1000);
    if (cached) {
      const next = normalizeTheme(cached);
      applyDomTheme(next.theme);
      return { ...next, ready: true };
    }
    return { theme: 'default', activatedAt: '', label: '', ready: false };
  });

  const applyThemePayload = useCallback((data) => {
    const next = normalizeTheme(data);
    applyDomTheme(next.theme);
    setState({ ...next, ready: true });
    return next;
  }, []);

  const refreshTheme = useCallback(async () => {
    try {
      const { data } = await axios.get(apiUrl('/api/public/theme'), { timeout: 6000 });
      try {
        sessionStorage.setItem(
          PREFETCH_KEYS.theme,
          JSON.stringify({ at: Date.now(), value: data })
        );
      } catch {
        /* ignore */
      }
      return applyThemePayload(data);
    } catch {
      setState((prev) => ({ ...prev, ready: true }));
      return null;
    }
  }, [applyThemePayload]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!cancelled) await refreshTheme();
    })();

    const onFocus = () => {
      refreshTheme();
    };
    const timer = window.setInterval(() => {
      refreshTheme();
    }, 45000);

    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshTheme]);

  const value = useMemo(
    () => ({
      theme: state.theme,
      activatedAt: state.activatedAt,
      label: state.label,
      ready: state.ready,
      refreshTheme,
    }),
    [state, refreshTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
